/**
 * Shared presentation vocabulary for the Conversations workspace and the thread header
 * (DIGEMP-GLOBAL-CONVERSATIONS-FRONTEND-072).
 *
 * Badge colors and Hebrew-relative time formatting are declared once here so the workspace list
 * and the single-thread header cannot drift apart — the same rule that made ConversationPanel the
 * single owner of the run-stage vocabulary. Server-computed labels (attention_label, status_label,
 * human_status) are rendered as given; this module only decides their look, never their truth.
 */
import { parseUtc } from "../utils/utcDate";

export const ATTENTION_BADGE = {
  needs_you: "bg-rose-100 text-rose-700",
  working: "bg-violet-100 text-violet-700",
  waiting_customer: "bg-sky-100 text-sky-700",
  settled: "bg-green-100 text-green-700",
  neutral: "bg-gray-100 text-gray-600",
  unknown: "bg-amber-100 text-amber-800",
};

export function attentionBadgeClass(attention) {
  return ATTENTION_BADGE[attention] || ATTENTION_BADGE.neutral;
}

/**
 * The ticket's own customer, or null when the row has already stated it.
 *
 * The thread projection ships the customer twice: `company_name` is the *thread's* customer, which
 * `conversation_center` resolves as `conversation.customer_id or ticket.company_id`, and
 * `ticket.company_name` is the linked ticket's own customer. The workspace row and the thread header
 * each drew the first on their `לקוח` chip and neither read the second, so a thread whose ticket
 * belongs to a different customer than the conversation rendered as though one customer owned both.
 *
 * The ordinary row must not print one name twice, so the ticket's name is returned only when it is
 * not the name already stated for the thread. Deciding that is the whole rule: this compares no
 * other pair and asserts no verdict — whether the two *should* agree is
 * `GET /api/agent-runs/{id}/context-binding`'s judgement, and it is not re-derived here.
 */
export function threadTicketCustomer(thread) {
  const onTicket = typeof thread?.ticket?.company_name === "string" ? thread.ticket.company_name.trim() : "";
  if (!onTicket) return null;
  const onThread = typeof thread?.company_name === "string" ? thread.company_name.trim() : "";
  return onTicket === onThread ? null : onTicket;
}

export function formatRelative(iso) {
  const date = parseUtc(iso);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "כרגע";
  if (minutes < 60) return `לפני ${minutes === 1 ? "דקה" : minutes === 2 ? "שתי דקות" : `${minutes} דקות`}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours === 1 ? "שעה" : hours === 2 ? "שעתיים" : `${hours} שעות`}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "לפני יום";
  if (days === 2) return "לפני יומיים";
  if (days < 7) return `לפני ${days} ימים`;
  return date.toLocaleDateString("he-IL");
}

export function formatFull(iso) {
  const date = parseUtc(iso);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleString("he-IL") : "";
}
